/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import styled from '@emotion/styled';
import { css as cssClassName } from 'emotion';
import React from 'react';

import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import Alert from 'react-bootstrap/lib/Alert';
import FormGroup from 'react-bootstrap/lib/FormGroup';
import InfoButton from '../../../../components/InfoButton';
import { trackEvent } from '../../../../modules/analytics';
import { MD_SCREEN_MIN, SM_SCREEN_MAX } from '../../../../modules/Styleguide';
import { CarrotIcon } from '../../../../shared/Icon/components/CarrotIcon';

import Box from '../../../../shared/Box';
import Heading from '../../../../shared/Heading';
import If from '../../../../shared/If';
import { Spinner } from '../../../../shared/Spinner';
import Text from '../../../../shared/Text';

import { useBrowserSize } from '../../../../contexts/BrowserSize';

// @ts-ignore
import styles from '../../../../components/OnboardingRouter/styles.sass';

import { cutoffAfterLengthWithElipsis } from '../../../../modules/StringFormatter';

type TypeaheadOption = {
  podcastName: string;
  podcastDescription: string;
  authorName: string;
  episodeCount: number;
  image: string;
  feedUrl: string;
  type: string;
};

type Props = {
  isSearching: boolean;
  onSelectTypeahead: (options: TypeaheadOption[]) => void;
  onTypeaheadSearch: (value: string) => void;
  rssTypeaheadResults: TypeaheadOption[];
  renderTypeaheadOption: (option: TypeaheadOption) => React.ReactNode;
  rssFeedError: {
    message: string;
  };
  hasDataToContinue: boolean;
  fetchingRSSFeedImport: boolean;
  rssFeedIsValid: boolean;
  onClickGetStarted: () => void;
  rssFeedMetadata: {
    authorName: string;
    authorEmail: string;
    image: string;
    isExplicit: boolean;
    itunesCategory: string;
    language: string;
    podcastName: string;
    podcastDescription: string;
    episodeCount: number;
  };
};

export const ImportForm = ({
  isSearching,
  onSelectTypeahead,
  onTypeaheadSearch,
  rssTypeaheadResults,
  renderTypeaheadOption,
  rssFeedError,
  hasDataToContinue,
  fetchingRSSFeedImport,
  rssFeedIsValid,
  onClickGetStarted,
  rssFeedMetadata,
}: Props) => {
  const { width } = useBrowserSize();

  const renderEpisodeCount = () => (
    <div
      css={css`
        @media (max-width: ${SM_SCREEN_MAX}px) {
          text-align: center;
        }
      `}
    >
      <Text size="md" color="#ffffff" isBold={true} isInline={true}>
        {rssFeedMetadata.episodeCount === 1
          ? `${rssFeedMetadata.episodeCount} EPISODE`
          : `${rssFeedMetadata.episodeCount} EPISODES`}
      </Text>
      <InfoButton
        direction="top"
        width={230}
        activeOnHover={false}
        infoBubbleClassName={cssClassName`
          box-shadow: none;
          display: block;
          font-size: 13px;
          line-height: 130%;
          &:before {
            box-shadow: none;
          }
        `}
        infoButtonIconClassName={cssClassName`
          background-color: #fff;
          color: #6c3bbf;
          margin: 0 5px;
        `}
      >
        This episode count might not reflect all of your episodes.
        <div>
          <a
            href="https://anch.co/episodes-found-incorrect"
            target="_blank"
            rel="noopener noreferrer"
            css={css`
              color: white;
              &:hover {
                color: white;
              }
            `}
          >
            Learn more
          </a>
          <CarrotIcon
            fillColor="#fff"
            className={cssClassName`width: 5px; margin-left: 5px;`}
          />
        </div>
      </InfoButton>
    </div>
  );

  return (
    <Box paddingBottom={57}>
      <FormGroup id="podcastName" bsClass={styles.podcastRSSField}>
        <AsyncTypeahead
          autoFocus={true}
          caseSensitive={true}
          emptyLabel="No podcasts or RSS feeds found."
          isLoading={isSearching}
          labelKey={option => option.podcastName}
          filterBy={() => true}
          onChange={onSelectTypeahead}
          onSearch={onTypeaheadSearch}
          options={isSearching ? [] : rssTypeaheadResults}
          placeholder={
            width < 768
              ? 'Enter podcast title / RSS'
              : 'Type in the name of your podcast OR paste in your RSS feed'
          }
          renderMenuItemChildren={renderTypeaheadOption}
        />
        {rssFeedError && rssFeedError.message && (
          <Alert bsStyle="danger">
            <Text color="#ffffff" size="sm" align="center">
              {rssFeedError.message}
            </Text>
          </Alert>
        )}
        {hasDataToContinue && !rssFeedIsValid && rssFeedError.message && (
          <Alert bsStyle="danger">
            <Text color="#ffffff" size="sm" align="center">
              Feed is not valid
            </Text>
          </Alert>
        )}
      </FormGroup>
      <Box marginTop={7}>
        <Text size="sm" align="center" color="#ffffff">
          Switching only takes 5 minutes. Learn more below, or get in touch with
          us{' '}
          <a
            href="https://anchorhq.typeform.com/to/QzuiVt"
            className={styles.typeform}
            target="_blank"
            rel="noopener noreferrer"
          >
            here
          </a>{' '}
          if you still have questions!
        </Text>
      </Box>
      <If
        condition={hasDataToContinue}
        ifRender={() => (
          <Box display="flex" alignItems="center" justifyContent="around">
            <SelectedPodcast>
              <PodcastImage src={rssFeedMetadata.image} />
              <SelectedPodcastInfo>
                <HeadingWrapper>
                  <Heading
                    size="xs"
                    color="#ffffff"
                    isBold={true}
                    smAlign="center"
                  >
                    {rssFeedMetadata.podcastName}
                  </Heading>
                  {width > SM_SCREEN_MAX && renderEpisodeCount()}
                </HeadingWrapper>
                <Text size="md" color="#ffffff" smAlign="center">
                  by {rssFeedMetadata.authorName}
                </Text>
                <Box marginTop={16}>
                  <Text size="md" smAlign="center">
                    <DescriptionText>
                      {rssFeedMetadata.podcastDescription}
                    </DescriptionText>
                  </Text>
                </Box>
                <ButtonWrapper>
                  <button
                    onClick={e => {
                      e.preventDefault();
                      onClickGetStarted();
                      trackEvent(
                        'importing_ux_get_started_button_clicked',
                        {},
                        // @ts-ignore
                        { providers: [mParticle] }
                      );
                    }}
                    className={`cyanButton ${styles.letsDoItButton}`}
                  >
                    Get started
                  </button>
                </ButtonWrapper>
                {width <= SM_SCREEN_MAX && renderEpisodeCount()}
              </SelectedPodcastInfo>
            </SelectedPodcast>
          </Box>
        )}
      />
      <If
        condition={fetchingRSSFeedImport}
        ifRender={() => (
          <Box
            width="100%"
            display="flex"
            justifyContent="center"
            marginTop={10}
            marginBottom={10}
          >
            <Spinner color="#ffffff" />
          </Box>
        )}
      />
    </Box>
  );
};

const SelectedPodcast = styled.div`
  width: 100%;
  display: flex;
  background-color: #6c3bbf;
  color: rgba(41, 47, 54, 0.3);
  padding: 25px;
  margin-top: 22px;
  border-radius: 6px;

  @media (max-width: ${SM_SCREEN_MAX}px) {
    flex-direction: column;
    align-items: center;
  }
`;

const HeadingWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  @media (max-width: ${SM_SCREEN_MAX}px) {
    display: block;
    margin: 24px 0 0;
  }
`;

const SelectedPodcastInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: 100%;
`;

const PodcastImage = styled.img`
  flex: 0 0 158px;
  width: 158px;
  height: 158px;
  border-radius: 6px;

  @media (min-width: ${MD_SCREEN_MIN}px) {
    margin-right: 24px;
  }
`;

const DescriptionText = styled.p`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  color: #fff;
`;

const ButtonWrapper = styled.div`
  width: 220px;
  margin: 16px 0 0;

  @media (max-width: ${SM_SCREEN_MAX}px) {
    width: 100%;
    margin: 24px 0;
  }
`;
